import { useEffect, useState } from "react";
import { trackFeature } from "@/lib/analytics";

/**
 * "Save Kin as a contact" block (Issue #308).
 *
 * WHY THIS EXISTS: US carriers can't show an alphanumeric sender name, so every
 * Kin text arrives from a bare +1 (567) 412-2302 and the first few trip iOS's
 * "may be spam" hint. If she saves the number as "Kin 💜" once, every future
 * text shows Kin's name and the spam hint disappears. This block is the nudge.
 *
 * TWO PATHS, GRACEFUL DEGRADATION (the spec's device-risk hedge):
 *  - Primary: a client-side .vcf download. Where the wrapper honors it (Android
 *    WebView, desktop, and iOS builds with download support) tapping opens the
 *    native "Add to Contacts" sheet with everything prefilled.
 *  - Fallback, always visible: tap-to-copy the raw number + a one-line "add it
 *    as Kin 💜" instruction — zero permissions, works everywhere even if the
 *    WKWebView swallows the download. No navigator.contacts, by design.
 *
 * `variant`:
 *  - "success": the loud version for the SmsSetupSheet "You're all set" screen.
 *  - "quiet": the small, dismissible Settings-card version for people who
 *    verified before this shipped. Dismissal persists in localStorage.
 */

// Exported because the opted-out state of the text block in HowKinReachesYou
// needs the same number for its copy chip — she has to text START to this exact
// number to turn texts back on, and two hardcoded copies of it is how they drift.
export const KIN_NUMBER_E164 = "+15674122302";
export const KIN_NUMBER_DISPLAY = "(567) 412-2302";
const DISMISS_KEY = "kin_save_contact_dismissed";

const INTRO_COPY = "One more thing: save my number as Kin 💜 so you always know it's me.";

/** RFC-6350 vCard for "Kin 💜". CRLF line breaks are what the spec wants and what iOS/Android parse most reliably. */
function buildVCard(): string {
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "N:;Kin 💜;;;",
    "FN:Kin 💜",
    "TEL;TYPE=CELL:" + KIN_NUMBER_E164,
    "ORG:KinYouDoItAll",
    "URL:https://kinyoudoitall.com",
    "END:VCARD",
  ].join("\r\n");
}

function downloadVCard() {
  const blob = new Blob([buildVCard()], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Kin.vcf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the wrapper a beat to hand the blob to the OS before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface SaveKinContactBlockProps {
  variant: "success" | "quiet";
}

const SaveKinContactBlock = ({ variant }: SaveKinContactBlockProps) => {
  const quiet = variant === "quiet";
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!quiet) return;
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      /* private mode / storage blocked — just show it */
    }
  }, [quiet]);

  if (quiet && dismissed) return null;

  const handleSave = () => {
    trackFeature("save_contact_vcard");
    try {
      downloadVCard();
    } catch {
      /* If the download path throws, the copy fallback below still works. */
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(KIN_NUMBER_E164);
      trackFeature("save_contact_copy_number");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — number is still shown for manual copy */
    }
  };

  const handleDismiss = () => {
    trackFeature("save_contact_dismissed");
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* storage blocked — dismiss for this session only */
    }
    setDismissed(true);
  };

  return (
    <div
      style={{
        position: "relative",
        marginTop: quiet ? 16 : 20,
        padding: quiet ? "12px 14px" : 16,
        borderRadius: 14,
        border: "1px solid #E8E6F4",
        background: "#F0F0FF",
      }}
    >
      {quiet && (
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex items-center justify-center"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "transparent",
            border: "none",
            fontSize: 12,
            color: "#9090A8",
            cursor: "pointer",
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      )}

      <p
        className="font-body"
        style={{
          fontSize: quiet ? 12.5 : 13,
          color: quiet ? "#54525C" : "#1C1A3E",
          lineHeight: 1.5,
          paddingRight: quiet ? 20 : 0,
        }}
      >
        {INTRO_COPY}
      </p>

      <button
        onClick={handleSave}
        className="font-body font-semibold text-white"
        style={{
          marginTop: quiet ? 10 : 14,
          padding: quiet ? "8px 14px" : "10px 18px",
          borderRadius: 999,
          fontSize: quiet ? 12.5 : 13,
          backgroundColor: "#5050C8",
          border: "none",
          cursor: "pointer",
        }}
      >
        Save Kin as a contact
      </button>

      <div
        className="font-body"
        style={{ fontSize: 11.5, color: "#54525C", marginTop: 12, lineHeight: 1.5 }}
      >
        Copy the number, then add it as a new contact named Kin 💜.
      </div>
      <button
        onClick={handleCopy}
        aria-label={"Copy Kin's number " + KIN_NUMBER_DISPLAY}
        className="font-body font-semibold"
        style={{
          marginTop: 6,
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 12.5,
          color: "#5050C8",
          background: "#FFFFFF",
          border: "1px solid #C8C8F4",
          cursor: "pointer",
        }}
      >
        {copied ? "✓ Copied!" : "📋 " + KIN_NUMBER_DISPLAY}
      </button>
    </div>
  );
};

export default SaveKinContactBlock;
